// ---------------------------------------------------------------------------
// WF-I: Workflow Step Execution Claims
// ---------------------------------------------------------------------------
// Pre-execution idempotency claim table. The action consumer INSERTs a claim
// row BEFORE invoking the domain handler. The UNIQUE constraint on
// (tenant_id, enrollment_id, node_id, attempt_idempotency_key) makes the
// claim atomic: a successful insert means "I own this attempt", a conflict
// means "another consumer already processed it; ack and move on".
//
// Why this is needed (WF-E F3 P2): Cloudflare Queue at-least-once delivery
// can re-deliver an already-processed message (consumer crash after handler
// success but before message.ack(), broker re-route, etc.). Webhook + email
// + messaging handlers all have observable side effects; firing twice
// produces duplicate emails, duplicate webhook deliveries, duplicate
// downstream events. Pre-execute INSERT ON CONFLICT DO NOTHING RETURNING
// gives us atomic single-execution semantics.
//
// Companion to `workflow_step_logs` (append-only history) — this table is
// the *claim*; the log is the *audit*. A single attempt produces exactly
// one row here and one or more rows in `workflow_step_logs` per status
// transition. The two are intentionally separate so the audit log remains
// append-only without conflicting on the claim's UNIQUE constraint.
//
// Tenant-scoped per CLAUDE.md MANDATORY rule: every query MUST include
// tenant_id in WHERE clause; the UNIQUE index leads with tenant_id for
// both isolation and index efficiency.

import { pgTable, uuid, text, timestamp, uniqueIndex } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const workflowStepExecutions = pgTable(
  "workflow_step_executions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    enrollmentId: text("enrollment_id").notNull(),
    workflowId: uuid("workflow_id").notNull(),
    nodeId: text("node_id").notNull(),
    actionRef: text("action_ref").notNull(),
    // Producer-supplied deterministic key — same retry of the same logical
    // attempt MUST produce the same key. Default convention used by the
    // state machine: `${causationId}:${attempt}`.
    attemptIdempotencyKey: text("attempt_idempotency_key").notNull(),
    claimedAt: timestamp("claimed_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_step_exec_attempt").on(
      table.tenantId,
      table.enrollmentId,
      table.nodeId,
      table.attemptIdempotencyKey,
    ),
  ],
);
